import styled from "@emotion/styled"
import { keyframes, css } from "@emotion/core"
import { breakpoints, theme } from "../../../utils/styles"

// Brand tokens for this section specifically (per design spec) — deliberately
// distinct from the sitewide theme.black/theme.primary tokens. The gold
// background is the one thing kept as the existing sitewide theme.primary.
export const INK = "#2b2820"
export const CARD = "#211d18"
export const GOLD_ACCENT = "#e8b84a"
export const ATOM = "#201c14"
export const FLASH = "#fff4d6"

// Opaque full-bleed backdrop behind the rounded card. The site's footer is
// position:fixed with a negative z-index, hidden behind normal page content
// and revealed only via a dedicated spacer near the true page bottom. The
// card's own margin below leaves gaps with nothing else painted in them —
// per CSS stacking rules a negative z-index fixed element still paints above
// the bare root (body) background, so without this backdrop the footer
// bleeds through those gaps at any scroll position. This wrapper gives the
// gap area real, non-root painted content so the footer stays hidden until
// its intended reveal point.
export const Backdrop = styled.div`
  position: relative;
  z-index: 1;
  background: ${theme.black};
  display: flow-root;
`

export const Container = styled.div`
  position: relative;
  isolation: isolate;
  overflow: hidden;
  background: ${theme.primary};
  border-radius: 32px;
  margin: 48px 32px;
  padding: 40px 0 64px;
  z-index: 1;

  @media (max-width: ${breakpoints.l}px) {
    margin: 24px 16px;
    border-radius: 20px;
    padding: 40px 0 56px;
  }
`

export const Content = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 6vw;
  display: flex;
  flex-direction: column;
`

export const HeadlineRow = styled.div`
  display: flex;
  align-items: stretch;
  gap: 40px;
  margin-bottom: 40px;

  @media (max-width: ${breakpoints.l}px) {
    flex-direction: column;
    gap: 16px;
    margin-bottom: 28px;
  }
`

export const TitleCol = styled.div`
  flex: 1.2;
`

export const Eyebrow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #4a3d10;

  &::before {
    content: "";
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${INK};
  }
`

export const Title = styled.h2`
  margin: 0;
  font-family: Nunito, sans-serif;
  font-weight: 800;
  font-size: clamp(24px, 3vw, 36px);
  line-height: 1.2;
  color: #1f1b13;

  span {
    display: block;
  }
`

export const LedeCol = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
`

export const Lede = styled.p`
  margin: 0;
  font-size: clamp(16px, 1.2vw, 18px);
  font-weight: 700;
  line-height: 1.6;
  color: #1f1b13;
`

// --- Flow visual: three animated stages connected by arrows, framed in a
// box whose outer edges line up with the result-card row below ---

export const FlowBox = styled.div`
  position: relative;
  z-index: 1;
  margin: 8px 0 40px;
  border: 1.5px solid rgba(43, 40, 32, 0.32);
  border-radius: 16px;
  padding: 28px 24px 20px;
  /* Frosted, not opaque: the corner molecule animation can reach up behind
     this box (it's part of the same section, deliberately). Without a fill
     here the falling molecules show through at full strength and clash with
     the stages/tags sitting right on top of them. This mutes whatever's
     behind while keeping the box's own content fully crisp. */
  background: ${theme.primary}E8;

  @media (max-width: ${breakpoints.l}px) {
    padding: 20px 16px 12px;
  }
`

export const FlowRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;

  @media (max-width: ${breakpoints.l}px) {
    flex-direction: column;
    align-items: center;
    gap: 8px;
    flex-wrap: nowrap;
  }
`

export const Stage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`

export const StageArrow = styled.div`
  display: flex;
  align-items: center;
  height: 180px;
  color: ${INK};
  opacity: 0.7;

  svg {
    width: 22px;
    height: 22px;
  }

  @media (max-width: ${breakpoints.l}px) {
    height: auto;
    transform: rotate(90deg);
    margin: 4px 0;
  }
`

export const StageTag = styled.div`
  margin-top: 16px;
  font-size: 12.5px;
  font-weight: 600;
  color: ${INK};
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  white-space: nowrap;

  span {
    opacity: 0.5;
  }
`

// --- Result cards ---

export const ResultCardsRow = styled.div`
  margin-top: 20px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  align-items: stretch;

  @media (max-width: ${breakpoints.l}px) {
    grid-template-columns: 1fr;
  }
`

export const ResultCard = styled.div`
  background: ${CARD};
  border-radius: 12px;
  padding: 20px;
  border-top: 3px solid ${GOLD_ACCENT};
  display: flex;
  flex-direction: column;
  transition: transform 0.35s cubic-bezier(0.16, 1, 0.3, 1);

  &:hover {
    transform: translateY(-4px);
  }
`

export const ResultTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;

  svg {
    width: 22px;
    height: 22px;
    stroke: ${GOLD_ACCENT};
  }
`

export const ResultNumber = styled.span`
  font-size: 12px;
  color: #8a7d52;
  font-weight: 600;
  font-family: Nunito, sans-serif;
`

export const ResultHeading = styled.h3`
  margin: 0 0 8px;
  font-weight: 700;
  font-size: 16px;
  color: ${GOLD_ACCENT};
  font-family: Nunito, sans-serif;
`

export const ResultBody = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.6;
  color: #cfc9bb;
`

export const NextButtonRow = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 56px;

  @media (max-width: ${breakpoints.l}px) {
    margin-top: 40px;
  }
`

export const NextButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 14px 28px;
  background: ${theme.black};
  color: ${theme.white};
  font-weight: 700;
  font-size: 15px;
  font-family: Nunito, sans-serif;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: transform 0.25s ease, box-shadow 0.25s ease;

  svg {
    width: 16px;
    height: 16px;
    transition: transform 0.25s ease;
  }

  &:hover {
    transform: translateY(3px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.25);
  }

  &:hover svg {
    transform: translateY(2px);
  }
`

// Corner field: bottom-left, tucked up so its top slightly overlaps
// behind the cards (z-index 0, cards are z-index 2). Clipped with
// overflow:hidden so it can never spill past its own box. Sized to
// hold the original animation's full throw range without clipping it.
export const CornerField = styled.div`
  position: absolute;
  left: 0;
  bottom: -20px;
  width: 560px;
  height: 600px;
  max-width: 55vw;
  overflow: hidden;
  z-index: 0;
  pointer-events: none;

  @media (max-width: ${breakpoints.l}px) {
    display: none;
  }
`

export const MoleculeGrid = styled.img`
  position: absolute;
  left: -108px;
  bottom: -112px;
`

const fallX = x => keyframes`
  0% {
    visibility: visible;
    transform: translateX(${x}px), rotate(0deg);
  }
  100% {
    visibility: visible;
    transform: translateX(${x + 200}px) rotate(360deg);
  }
`

const fallY = y => keyframes`
  0% {
    transform: translateY(${y}px);
  }
  40% {
    transform: translateY(${y - 200}px);
  }
  100% {
    transform: translateY(${y - 100}px);
  }
`

export const FallOne = styled.div`
  animation: ${({ animate }) =>
    animate
      ? css`
          ${fallY(400)} 3s forwards ease-in
        `
      : "none"};
`

export const MoleculeOne = styled.img`
  visibility: hidden;
  animation: ${({ animate }) =>
    animate
      ? css`
          ${fallX(120)} 3s forwards ease-in;
        `
      : "none"};
`

export const FallTwo = styled.div`
  animation: ${({ animate }) =>
    animate
      ? css`
          ${fallY(100)} 3s forwards ease-in
        `
      : "none"};
`

export const MoleculeTwo = styled.img`
  visibility: hidden;
  animation: ${({ animate }) =>
    animate
      ? css`
          ${fallX(60)} 3s forwards ease-in;
        `
      : "none"};
`

export const FallThree = styled.div`
  animation: ${({ animate }) =>
    animate
      ? css`
          ${fallY(150)} 3s forwards ease-in
        `
      : "none"};
`

export const MoleculeThree = styled.img`
  visibility: hidden;
  animation: ${({ animate }) =>
    animate
      ? css`
          ${fallX(20)} 3s forwards ease-in;
        `
      : "none"};
`

export const FallFour = styled.div`
  animation: ${({ animate }) =>
    animate
      ? css`
          ${fallY(180)} 3s forwards ease-in
        `
      : "none"};
`

export const MoleculeFour = styled.img`
  visibility: hidden;
  animation: ${({ animate }) =>
    animate
      ? css`
          ${fallX(60)} 3s forwards ease-in;
        `
      : "none"};
`

export const FallFive = styled.div`
  animation: ${({ animate }) =>
    animate
      ? css`
          ${fallY(-200)} 3s forwards ease-in
        `
      : "none"};
`

export const MoleculeFive = styled.img`
  visibility: hidden;
  animation: ${({ animate }) =>
    animate
      ? css`
          ${fallX(-100)} 3s forwards ease-in;
        `
      : "none"};
`
